import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';

// Define the product type
export interface Product {
  _id: string;
  name: string;
  units: {
    type: string;
    price: string;
    quantity?: string | number
  }[];
  images: string[];
}

// Define the context shape
interface CartContextType {
  cart: Product[];
  addToCart: (product: Product, unitType: string) => void;
  onDecreaseQuantity: (product: Product, unitType: string) => void;
  removeFromCart: (productId: string | number) => void;
  clearCart: () => void;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
}

// Create context with default value (undefined for type safety)
const CartContext = createContext<CartContextType | undefined>(undefined);

// Props for the CartProvider
interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [cart, setCart] = useState<Product[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Load cart from localStorage on first mount
  useEffect(() => {
    
    if (typeof window !== 'undefined') {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        setCart(JSON.parse(savedCart));
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('cart', JSON.stringify(cart));
    }
  }, [cart]);

  const addToCart = (product: Product, unitType: string) => {
    setCart((prev) => {
      const existing = prev.find((item) => item._id === product._id);

      if (existing) {
        return prev.map((item) =>
          item._id === product._id
            ? {
              ...item,
              units: item.units.map((unit: any) => {
                if (unit.type === unitType) {
                  unit.quantity = Number(unit.quantity || 0) + 1;
                } 
                return unit;
              })
            } : item
        );
      }

      return [...prev, { 
        ...product,
        units: product.units.map((unit: any) => {
          if (unit.type === unitType) {
            unit.quantity = 1;
          } 
          return unit;
        })
      }];
    });
  };

  const onDecreaseQuantity = (product: Product, unitType: string) => {
    setCart((prev) => {
      const existing = prev.find((item) => item._id === product._id);

      if (existing) {
        return prev.map((item) =>
          item._id === product._id
            ? {
              ...item,
              units: item.units.map((unit: any) => {
                if (unit.type === unitType) {
                  const newQuantiy = Number(unit.quantity || 0) - 1;
                  unit.quantity = newQuantiy < 1 ? 1 : newQuantiy;
                } 
                return unit;
              })
            } : item
        );
      }

      return [...prev];
    });
  };

  const removeFromCart = (productId: string | number) => {
    setCart((prev) => prev.filter((item) => item._id !== productId));
  };

  const clearCart = () => setCart([]);

  return (
    <CartContext.Provider 
      value={{ 
        cart, 
        addToCart, 
        onDecreaseQuantity,
        removeFromCart, 
        clearCart ,
        isCartOpen,
        setIsCartOpen
      }}
    >
      {children}
    </CartContext.Provider>
  );  
};

// Custom hook to use the cart context
export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
